import React from 'react';
import { useLanguage } from '../../hooks/useLanguage';

interface HelpWindowProps {
    windowId: string;
    title: string;
    onClose: () => void;
}

// Composant d'aide par défaut
const DefaultHelp: React.FC = () => {
    const { t } = useLanguage();
    
    return (
        <div className="text-slate-200 font-mono text-sm space-y-4">
            <div className="text-center">
                <h2 className="text-cyan-400 font-bold text-base mb-4 uppercase tracking-wide">
                    {t('helpWindow.default.title')}
                </h2>
                <div className="text-slate-400 text-xs mb-6">
                    {t('helpWindow.default.inDevelopment')}
                </div>
            </div>
            
            <div className="border border-slate-600 p-4 bg-slate-800/50">
                <div className="text-cyan-300 font-bold text-xs mb-2">{t('helpWindow.default.systemInfo')}</div>
                <div className="text-xs space-y-1">
                    <div>{t('helpWindow.default.version')} : ALBION_V1.2</div>
                    <div>{t('helpWindow.default.status')} : {t('helpWindow.default.operational')}</div>
                    <div>{t('helpWindow.default.type')} : {t('helpWindow.default.genericModule')}</div>
                </div>
            </div>
            
            <div className="text-center text-slate-500 text-xs">
                {t('helpWindow.default.detailedContent')}
                <br />
                {t('helpWindow.default.consultDoc')}
            </div>
        </div>
    );
};

// Composant d'aide pour le tableau d'enquête
const BoardHelp: React.FC = () => {
    const { t } = useLanguage();
    
    return (
        <div className="text-slate-200 font-mono text-sm space-y-6">
            
            {/* Section Navigation */}
            <div className="border-b border-slate-600 pb-4">
                <h2 className="text-cyan-400 font-bold text-base mb-3 uppercase tracking-wide">{t('helpWindow.board.navigation.title')}</h2>
                <div className="space-y-2 text-xs">
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.navigation.move')} :</span> {t('helpWindow.board.navigation.moveDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.navigation.zoom')} :</span> {t('helpWindow.board.navigation.zoomDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.navigation.realign')} :</span> {t('helpWindow.board.navigation.realignDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.navigation.splitMode')} :</span> {t('helpWindow.board.navigation.splitModeDesc')}</div>
                </div>
            </div>

            {/* Section Éléments */}
            <div className="border-b border-slate-600 pb-4">
                <h2 className="text-cyan-400 font-bold text-base mb-3 uppercase tracking-wide">{t('helpWindow.board.elements.title')}</h2>
                <div className="space-y-2 text-xs">
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.elements.add')} :</span> {t('helpWindow.board.elements.addDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.elements.move')} :</span> {t('helpWindow.board.elements.moveDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.elements.selection')} :</span> {t('helpWindow.board.elements.selectionDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.elements.deletion')} :</span> {t('helpWindow.board.elements.deletionDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.elements.opacity')} :</span> {t('helpWindow.board.elements.opacityDesc')}</div>
                </div>
            </div>

            {/* Section Connexions */}
            <div className="border-b border-slate-600 pb-4">
                <h2 className="text-cyan-400 font-bold text-base mb-3 uppercase tracking-wide">{t('helpWindow.board.connections.title')}</h2>
                <div className="space-y-2 text-xs">
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.connections.createLink')} :</span> {t('helpWindow.board.connections.createLinkDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.connections.cutLink')} :</span> {t('helpWindow.board.connections.cutLinkDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.connections.colors')} :</span> {t('helpWindow.board.connections.colorsDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.connections.resonance')} :</span> {t('helpWindow.board.connections.resonanceDesc')}</div>
                </div>
            </div>

            {/* Section Documents */}
            <div className="border-b border-slate-600 pb-4">
                <h2 className="text-cyan-400 font-bold text-base mb-3 uppercase tracking-wide">{t('helpWindow.board.documents.title')}</h2>
                <div className="space-y-2 text-xs">
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.documents.grouping')} :</span> {t('helpWindow.board.documents.groupingDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.documents.ungrouping')} :</span> {t('helpWindow.board.documents.ungroupingDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.documents.navigation')} :</span> {t('helpWindow.board.documents.navigationDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.documents.extraction')} :</span> {t('helpWindow.board.documents.extractionDesc')}</div>
                </div>
            </div>

            {/* Section Annotations */}
            <div className="border-b border-slate-600 pb-4">
                <h2 className="text-cyan-400 font-bold text-base mb-3 uppercase tracking-wide">{t('helpWindow.board.annotations.title')}</h2>
                <div className="space-y-2 text-xs">
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.annotations.create')} :</span> {t('helpWindow.board.annotations.createDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.annotations.edit')} :</span> {t('helpWindow.board.annotations.editDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.annotations.navigation')} :</span> {t('helpWindow.board.annotations.navigationDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.annotations.deletion')} :</span> {t('helpWindow.board.annotations.deletionDesc')}</div>
                </div>
            </div>

            {/* Section Fonctions Avancées */}
            <div className="border-b border-slate-600 pb-4">
                <h2 className="text-cyan-400 font-bold text-base mb-3 uppercase tracking-wide">{t('helpWindow.board.advanced.title')}</h2>
                <div className="space-y-2 text-xs">
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.advanced.opticalOverlay')} :</span> {t('helpWindow.board.advanced.opticalOverlayDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.advanced.chronogram')} :</span> {t('helpWindow.board.advanced.chronogramDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.advanced.viewer')} :</span> {t('helpWindow.board.advanced.viewerDesc')}</div>
                    <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.advanced.save')} :</span> {t('helpWindow.board.advanced.saveDesc')}</div>
                </div>
            </div>

            {/* Section Raccourcis */}
            <div>
                <h2 className="text-cyan-400 font-bold text-base mb-3 uppercase tracking-wide">{t('helpWindow.board.shortcuts.title')}</h2>
                <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="space-y-1">
                        <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.shortcuts.delete')} :</span> {t('helpWindow.board.shortcuts.deleteDesc')}</div>
                        <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.shortcuts.wheel')} :</span> {t('helpWindow.board.shortcuts.wheelDesc')}</div>
                    </div>
                    <div className="space-y-1">
                        <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.shortcuts.rightClick')} :</span> {t('helpWindow.board.shortcuts.rightClickDesc')}</div>
                        <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.shortcuts.doubleClick')} :</span> {t('helpWindow.board.shortcuts.doubleClickDesc')}</div>
                        <div><span className="text-cyan-300 font-bold">{t('helpWindow.board.shortcuts.enterEsc')} :</span> {t('helpWindow.board.shortcuts.enterEscDesc')}</div>
                    </div>
                </div>
            </div>

        </div>
    );
};

/**
 * Composant HelpWindow - Fenêtre d'aide générique pour tous les modules
 * Affiche le contenu d'aide spécifique à chaque fenêtre
 */
export const HelpWindow: React.FC<HelpWindowProps> = ({ windowId, title, onClose }) => {
    const { t } = useLanguage();
    
    // Fonction pour rendre le contenu d'aide approprié
    const renderHelpContent = () => {
        switch (windowId) {
            case 'board':
                return <BoardHelp />;
            case 'explorer':
            case 'logs':
            case 'messenger':
            default:
                return <DefaultHelp />;
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center"
            onClick={onClose} // Fermer si on clique sur le fond
        >
            <div 
                className="relative w-[800px] h-[600px] bg-slate-800 border-2 border-slate-600 shadow-2xl shadow-black/50 flex flex-col"
                onClick={(e) => e.stopPropagation()} // Empêcher la fermeture au clic sur la fenêtre
            >
                {/* Barre de titre */}
                <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white border-b-2 border-cyan-300 px-4 py-2 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="font-bold tracking-wider uppercase text-sm font-mono">{t('helpWindow.title')}_{title}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-6 h-6 flex items-center justify-center bg-slate-500 border-2 border-slate-400 text-white text-xs hover:bg-red-500 transition-colors font-bold"
                    >
                        {t('helpWindow.close')}
                    </button>
                </div>
                
                {/* Contenu de l'aide */}
                <div className="flex-1 bg-slate-900 border-2 border-slate-600 m-1 overflow-hidden">
                    <div className="h-full overflow-y-scroll p-4" style={{ scrollbarWidth: 'thin' }}>
                        {renderHelpContent()}
                    </div>
                </div>
                
                {/* Pied de page */}
                <div className="bg-slate-800 border-t border-slate-600 px-4 py-2 text-center">
                    <div className="text-[8px] text-slate-500 font-mono opacity-50">
                        {t('helpWindow.footer')}_{windowId.toUpperCase()}
                    </div>
                </div>
            </div>
        </div>
    );
};